import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { Alert } from '@/components/Alert'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_CANCELRESTORE_COPY } from '@/copy/modals'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type JobRef = components['schemas']['JobRef']
type RestoreJob = components['schemas']['RestoreJob']

/** Minutes elapsed since a `MachineTime` ISO instant, for the subheading's "running N minutes" —
 * a plain formatted-timestamp difference, not a served aggregate figure (§24.2 rule 24 governs
 * counts like `Ratio`, not a relative-time caption). */
function minutesSince(startedAt: string): number {
  return Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 60000))
}

/**
 * `m-cancelrestore` — process/09-SCREEN-SPECS/m-cancelrestore.md. `restoreKindLabel` is the
 * caller's own (the restore wizard's, Pass 6, not built yet) — `JobRef.kind` only ever carries
 * the generic word "Restore", never which of §18.3's eight kinds this job is.
 *
 * **`note` added W159** — `outcomeNote` was already rendered, as a raw `<p>` instead of the shared
 * `Note` component.
 */
export function MCancelrestore({
  orgId,
  workspaceId,
  job,
  restoreKindLabel,
  onDismiss,
  onCancelled,
}: {
  orgId: string
  workspaceId: string
  job: JobRef
  restoreKindLabel: string
  onDismiss: () => void
  onCancelled: (job: RestoreJob) => void
}) {
  const [reason, setReason] = useState('')

  const cancel = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/cancel', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: job.job_id } },
        body: reason ? { reason } : undefined,
      })
      return unwrap(response as { data?: RestoreJob; error?: Problem })
    },
    onSuccess: onCancelled,
  })

  const problem = cancel.error instanceof ApiProblemError ? cancel.error.problem : undefined
  const headingId = 'm-cancelrestore-heading'

  return (
    <Modal dataScr={unitId('m-cancelrestore')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={480}>
      <ModalHeader id={headingId} heading={M_CANCELRESTORE_COPY.headingWithHandleTemplate(job.handle)}>
        {M_CANCELRESTORE_COPY.runningForTemplate(restoreKindLabel, `${minutesSince(job.started_at)} minutes`)}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}
        <Alert variant="warn">{M_CANCELRESTORE_COPY.notUndoingWarning}</Alert>
        <div className="mt-6 flex flex-col gap-5">
          <Strip>{M_CANCELRESTORE_COPY.betweenObjectsNote}</Strip>
          <Strip>{M_CANCELRESTORE_COPY.fullReportNote}</Strip>
          <Strip>{M_CANCELRESTORE_COPY.protectionWriteNote}</Strip>
        </div>
        <div className="mt-6">
          <Note>{M_CANCELRESTORE_COPY.outcomeNote}</Note>
        </div>
        <div className="mt-6">
          <Field label={M_CANCELRESTORE_COPY.reasonLabel}>
            <input
              type="text"
              value={reason}
              maxLength={255}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg"
            />
          </Field>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_CANCELRESTORE_COPY.keepRunningButton}
        </Button>
        <Button data-act={actionId('restore.cancel')} danger disabled={cancel.isPending} onClick={() => cancel.mutate()}>
          {M_CANCELRESTORE_COPY.cancelButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
